// React and Redux imports
import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// Material-UI imports
import { Link, Grid, TooltipProps } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'

// Custom components and helper imports
import DataGridWithSearchV2 from 'components/generic/datagrid/DataGridWithSearchV2'

// Service imports
import { useGetInvoicePaymentsQuery } from 'services/invoicePayments'

// Utility and function imports
import { formatNumber } from 'utils/helpers'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { InvoicePayment } from '../../types'
import { SORTABLE_INVOICE_PAYMENTS_FIELDS } from 'utils/constants'
import { generateRenderHeaderFunction } from 'utils/generateRenderHeaderFunction'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
}))

export interface ButtonData {
  icon?: React.ReactNode
  type: string
  text?: string | React.ReactNode
  onClick: (navigateOrOption: string | (() => void)) => void
  id: string
  tooltipProps?: TooltipProps
}

interface InvoicePaymentsContainerProps extends React.ComponentPropsWithoutRef<'div'> {
  textSearchPlaceholder?: string
  customPageSize?: number
  actionButtons?: ButtonData[]
  pricePrepend?: string
}

const InvoicePaymentsContainer: React.FC<InvoicePaymentsContainerProps> = ({
  actionButtons = [],
  pricePrepend = '$',
  ...restProps
}) => {
  const initialFilter = {
    search: '',
    customerName: '',
    paymentNumber: '',
    paymentDate: '',
    invoiceNumber: '',
    minSplits: 2,
    sort: {
      field: SORTABLE_INVOICE_PAYMENTS_FIELDS.PAYMENT_DATE,
      ascending: false,
    },
  }
  const navigate = useNavigate()
  const classes = useStyles()
  const [paginationOptions, setPaginationOptions] = useState({ page: 1, pageSize: 10 })
  const [filter, setFilter] = useState(initialFilter)

  const formatAmount = useCallback(
    amount => {
      return `${pricePrepend}${formatNumber((Number(amount) || 0).toFixed(2))}`
    },
    [pricePrepend],
  )

  const formatPaymentMethod = useCallback(paymentType => {
    return paymentType ? paymentType.charAt(0).toUpperCase() + paymentType.slice(1).toLowerCase() : 'N/A'
  }, [])

  const { data: invoicePaymentsData, isFetching } = useGetInvoicePaymentsQuery({
    page: paginationOptions.page,
    pageSize: paginationOptions.pageSize,
    filter: JSON.stringify({
      ...filter,
      minSplits: 2,
    }),
  })

  const rows: InvoicePayment[] = useMemo(() => {
    return (invoicePaymentsData?.Items || []).map(payment => {
      return {
        id: payment.id,
        paymentNumber: payment.paymentNumber,
        customerName: payment.customerName,
        invoices: payment.paymentSplits.length,
        amount: formatAmount(payment.amount),
        paymentMethod: formatPaymentMethod(payment.paymentType),
        paymentDate: payment.paymentDate,
        targetInvoiceId: payment.paymentSplits[0].targetInvoiceId,
      }
    })
  }, [invoicePaymentsData, formatAmount, formatPaymentMethod])

  const handlePaymentClick = useCallback(
    payment => {
      navigate(`/app/sales/invoice-payments/${payment.id}`)
    },
    [navigate],
  )

  const renderHeaderWithSort = useMemo(() => generateRenderHeaderFunction(filter, setFilter), [filter, setFilter])

  const columns = useMemo(
    () => [
      {
        field: 'paymentNumber',
        headerName: 'Payment #',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        renderCell: params => (
          <Link
            component="button"
            variant="body2"
            onClick={() => handlePaymentClick(params.row)}
            style={{ color: '#009F4D' }}
            data-testid={`payment-number-${params.row.id}`}
          >
            {params.value || ''}
          </Link>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        valueGetter: () => 'Paid',
        renderCell: params => <span data-testid={`status-${params.row.id}`}>Paid</span>,
      },
      {
        field: 'customerName',
        headerName: 'Customer',
        flex: 5,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        renderCell: params => <span data-testid={`customer-name-${params.row.id}`}>{params.value}</span>,
      },
      {
        field: 'invoices',
        headerName: 'Invoices',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        renderCell: params => <span data-testid={`invoices-${params.row.id}`}>{params.value}</span>,
      },
      {
        field: 'amount',
        headerName: 'Amount',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        renderCell: params => <span data-testid={`amount-${params.row.id}`}>{params.value}</span>,
      },
      {
        field: 'paymentMethod',
        headerName: 'Payment Method',
        flex: 1.25,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        renderCell: params => <span data-testid={`payment-method-${params.row.id}`}>{params.value}</span>,
      },
      {
        field: 'paymentDate',
        headerName: 'Payment Date',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        valueGetter: params => new Date(params.row.paymentDate),
        renderCell: params => (
          // params.row.paymentDate does not have a time, so the Date constructor treats it as 00:00:00 GMT
          // we need to use 'GMT' as the timezone in dateFormatter so that the date does not get converted into the "local" date
          <span data-testid={`payment-date-${params.row.id}`}>
            {dateFormatter(new Date(params.row.paymentDate), 'GMT')}
          </span>
        ),
        renderHeader: () =>
          renderHeaderWithSort('Payment Date', SORTABLE_INVOICE_PAYMENTS_FIELDS.PAYMENT_DATE, {
            startAscending: false,
          }),
      },
    ],
    [handlePaymentClick, renderHeaderWithSort],
  )

  return (
    <Grid p={3} sx={{ height: '100%' }} container flexDirection="column" data-testid="invoice-payments-container">
      <DataGridWithSearchV2
        rowCount={invoicePaymentsData?.resultsReturned || 0}
        className={classes.root}
        rows={rows}
        columns={columns}
        loading={isFetching}
        useBackendSortAndFilter
        filter={filter}
        setFilter={setFilter}
        actionButtons={actionButtons}
        paginationMode="server"
        rowsPerPageOptions={[10, 20, 30, 50]}
        data-testid="invoice-payments-data-grid"
        onPageChange={newPageNumber => setPaginationOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))}
        onPageSizeChange={newPageSize => setPaginationOptions(prevState => ({ ...prevState, pageSize: newPageSize }))}
        pageSize={paginationOptions.pageSize}
        autoPageSize={false}
        {...(restProps as any)}
      />
    </Grid>
  )
}

export default InvoicePaymentsContainer
